import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTestingAgentClient } from "../api/TestingAgentApiClient";
import type { SessionResponse } from "../api/models/ApiModels";
import { useTelemetry } from "../telemetry/useTelemetry";
import ScoutReportView from "./ScoutReportView";

interface ScoutReportPageProps {
    resourceId: string;
    projectId: string;
    sessionId: string;
}

/**
 * Standalone page that fetches a single scout session by ID
 * and renders the ScoutReportView. Used for deep-linking from PRs.
 */
const ScoutReportPage: React.FC<ScoutReportPageProps> = ({ resourceId, projectId, sessionId }) => {
    const navigate = useNavigate();
    const { getSession } = useTestingAgentClient();
    const { track } = useTelemetry();
    const [session, setSession] = React.useState<SessionResponse | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const s = await getSession(resourceId, projectId, sessionId);
                if (!cancelled) { setSession(s); setLoading(false); track("scout_report_viewed", { sessionId, projectId }); }
            } catch (err) {
                if (!cancelled) { setError(err instanceof Error ? err.message : String(err)); setLoading(false); }
            }
        })();
        return () => { cancelled = true; };
    }, [resourceId, projectId, sessionId, getSession, track]);

    const handleClose = React.useCallback(() => {
        navigate(`/dashboard/${encodeURIComponent(resourceId)}/${encodeURIComponent(projectId)}`);
    }, [navigate, resourceId, projectId]);

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "#8b949e", background: "#0d1117" }}>
                Loading scout report…
            </div>
        );
    }

    if (error || !session) {
        return (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", color: "#f85149", background: "#0d1117", gap: 16 }}>
                <div>{error ?? "Session not found"}</div>
                <button
                    onClick={handleClose}
                    style={{ padding: "8px 16px", background: "#21262d", color: "#c9d1d9", border: "1px solid #30363d", borderRadius: 6, cursor: "pointer" }}
                >
                    ← Back to Dashboard
                </button>
            </div>
        );
    }

    return <ScoutReportView session={session} onClose={handleClose} projectId={projectId} />;
};

export default ScoutReportPage;
